import React, { useEffect, useState } from 'react';
import {
  getCompanies,
  getBankStatements,
  getBankStatementDetail,
  uploadBankStatement,
  autoMatchTransactions,
  manualMatchTransaction,
  ignoreTransaction,
  getReconciliationSummary,
  getInvoices
} from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Building2,
  Upload,
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ArrowRight,
  Link2,
  Ban,
  Search,
  CreditCard,
  Receipt,
  Landmark,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  if (value === undefined || value === null) return '0';
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const BankReconciliationPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [statements, setStatements] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [invoices, setInvoices] = useState([]);

  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadStatements();
      loadSummary();
      loadInvoices();
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadStatements = async () => {
    try {
      const response = await getBankStatements(selectedCompany);
      setStatements(response.data);
    } catch (error) {
      console.error('Failed to load statements:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await getReconciliationSummary(selectedCompany);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await getInvoices();
      setInvoices(response.data.filter(inv => inv.company_id === selectedCompany));
    } catch (error) {
      console.error('Failed to load invoices:', error);
    }
  };

  const loadStatementDetail = async (statementId) => {
    setLoading(true);
    try {
      const response = await getBankStatementDetail(statementId);
      setSelectedStatement(response.data);
    } catch (error) {
      toast.error('Failed to load statement details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !bankName) {
      toast.error('Please provide file and bank name');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('company_id', selectedCompany);
    formData.append('bank_name', bankName);
    if (accountNumber) formData.append('account_number', accountNumber);

    setLoading(true);
    try {
      const response = await uploadBankStatement(formData);
      toast.success(`Uploaded ${response.data.transactions_parsed} transactions`);
      setUploadDialogOpen(false);
      setUploadFile(null);
      setBankName('');
      setAccountNumber('');
      loadStatements();
      loadSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoMatch = async (statementId) => {
    setLoading(true);
    try {
      const response = await autoMatchTransactions(statementId);
      toast.success(`Matched ${response.data.newly_matched} transactions`);
      loadStatementDetail(statementId);
      loadStatements();
      loadSummary();
    } catch (error) {
      toast.error('Auto-match failed');
    } finally {
      setLoading(false);
    }
  };

  const handleManualMatch = async (invoiceId) => {
    if (!selectedTransaction || !selectedStatement) return;

    try {
      await manualMatchTransaction(selectedStatement.id, selectedTransaction.id, {
        match_type: 'invoice',
        match_id: invoiceId
      });
      toast.success('Transaction matched');
      setMatchDialogOpen(false);
      loadStatementDetail(selectedStatement.id);
      loadStatements();
      loadSummary();
    } catch (error) {
      toast.error('Match failed');
    }
  };

  const handleIgnore = async (transactionId, reason) => {
    if (!selectedStatement) return;

    try {
      await ignoreTransaction(selectedStatement.id, transactionId, reason);
      toast.success('Transaction ignored');
      loadStatementDetail(selectedStatement.id);
      loadStatements();
      loadSummary();
    } catch (error) {
      toast.error('Failed to ignore transaction');
    }
  };

  const openMatchDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setMatchDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="bank-recon-title">
            Bank Reconciliation
          </h1>
          <p className="text-muted-foreground mt-1">
            Import statements, match transactions, reconcile accounts
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select Company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Statement
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-5 gap-4">
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Total Statements</p>
            <p className="text-2xl font-bold">{summary.total_statements}</p>
          </Card>
          <Card className="p-4 bg-green-50 border-green-200">
            <p className="text-sm text-green-700">Reconciled</p>
            <p className="text-2xl font-bold text-green-800">{summary.reconciled}</p>
          </Card>
          <Card className="p-4 bg-amber-50 border-amber-200">
            <p className="text-sm text-amber-700">In Progress</p>
            <p className="text-2xl font-bold text-amber-800">{summary.in_progress}</p>
          </Card>
          <Card className="p-4 bg-blue-50 border-blue-200">
            <p className="text-sm text-blue-700">Matched Transactions</p>
            <p className="text-2xl font-bold text-blue-800">{summary.total_matched}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Reconciliation Rate</p>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold">{summary.reconciliation_rate}%</p>
              <Progress value={summary.reconciliation_rate} className="w-16 h-2" />
            </div>
          </Card>
        </div>
      )}

      <Tabs defaultValue="statements" className="space-y-4">
        <TabsList>
          <TabsTrigger value="statements">Statements</TabsTrigger>
          <TabsTrigger value="transactions" disabled={!selectedStatement}>
            Transactions {selectedStatement && `(${selectedStatement.transaction_count})`}
          </TabsTrigger>
        </TabsList>

        {/* Statements Tab */}
        <TabsContent value="statements" className="space-y-4">
          <Card className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Bank Statements</h3>
              <Button variant="outline" onClick={loadStatements}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>

            {statements.length > 0 ? (
              <div className="space-y-3">
                {statements.map(stmt => (
                  <div
                    key={stmt.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedStatement?.id === stmt.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => loadStatementDetail(stmt.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <Landmark className="h-5 w-5 text-blue-600" />
                          <span className="font-semibold">{stmt.bank_name}</span>
                          {stmt.account_number && (
                            <span className="text-sm text-muted-foreground">({stmt.account_number})</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{stmt.filename}</p>
                      </div>
                      <Badge className={
                        stmt.status === 'reconciled' ? 'bg-green-100 text-green-800' :
                        stmt.status === 'in_progress' ? 'bg-amber-100 text-amber-800' :
                        'bg-slate-100 text-slate-800'
                      }>
                        {stmt.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-4 mt-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Transactions</p>
                        <p className="font-semibold">{stmt.transaction_count}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Matched</p>
                        <p className="font-semibold text-green-600">{stmt.matched_count}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Unmatched</p>
                        <p className="font-semibold text-amber-600">{stmt.unmatched_count}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Uploaded</p>
                        <p className="font-semibold">{new Date(stmt.upload_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-3 pt-3 border-t">
                      <div className="text-sm">
                        <span className="text-green-600 mr-4">Credits: {formatCurrency(stmt.total_credits)}</span>
                        <span className="text-red-600">Debits: {formatCurrency(stmt.total_debits)}</span>
                      </div>
                      {stmt.status !== 'reconciled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleAutoMatch(stmt.id); }}
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          Auto-Match
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No bank statements uploaded yet</p>
                <Button className="mt-4" onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Statement
                </Button>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          {selectedStatement && (
            <>
              <Card className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold">{selectedStatement.bank_name} - {selectedStatement.filename}</h3>
                    <p className="text-sm text-muted-foreground">
                      {selectedStatement.transaction_count} transactions • {selectedStatement.unmatched_count} unmatched
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => loadStatementDetail(selectedStatement.id)}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                    {selectedStatement.unmatched_count > 0 && (
                      <Button onClick={() => handleAutoMatch(selectedStatement.id)}>
                        <Link2 className="h-4 w-4 mr-2" />
                        Auto-Match All
                      </Button>
                    )}
                  </div>
                </div>
              </Card>

              {/* Unmatched Transactions */}
              {selectedStatement.transactions?.filter(t => t.status === 'unmatched').length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-4 text-amber-700 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Unmatched Transactions ({selectedStatement.transactions.filter(t => t.status === 'unmatched').length})
                  </h3>
                  <div className="space-y-2">
                    {selectedStatement.transactions
                      .filter(t => t.status === 'unmatched')
                      .map(txn => (
                        <div key={txn.id} className="p-3 border rounded-lg flex justify-between items-center hover:bg-slate-50">
                          <div className="flex items-center gap-4">
                            {txn.type === 'credit' ? (
                              <TrendingUp className="h-5 w-5 text-green-500" />
                            ) : (
                              <TrendingDown className="h-5 w-5 text-red-500" />
                            )}
                            <div>
                              <p className="font-medium">{txn.description}</p>
                              <p className="text-sm text-muted-foreground">{txn.date} • {txn.reference}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <p className={`text-lg font-bold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {txn.type === 'credit' ? '+' : '-'}{formatCurrency(txn.amount)}
                            </p>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => openMatchDialog(txn)}>
                                <Link2 className="h-4 w-4 mr-2" />
                                Match
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleIgnore(txn.id, 'Manual ignore')}>
                                <Ban className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </Card>
              )}

              {/* Matched Transactions */}
              {selectedStatement.transactions?.filter(t => t.status === 'matched').length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-4 text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    Matched Transactions ({selectedStatement.transactions.filter(t => t.status === 'matched').length})
                  </h3>
                  <div className="space-y-2">
                    {selectedStatement.transactions
                      .filter(t => t.status === 'matched')
                      .map(txn => (
                        <div key={txn.id} className="p-3 border rounded-lg flex justify-between items-center bg-green-50">
                          <div className="flex items-center gap-4">
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            <div>
                              <p className="font-medium">{txn.description}</p>
                              <p className="text-sm text-muted-foreground">
                                Matched to {txn.matched_type}: {txn.matched_reference || txn.matched_to}
                              </p>
                            </div>
                          </div>
                          <p className={`text-lg font-bold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.type === 'credit' ? '+' : '-'}{formatCurrency(txn.amount)}
                          </p>
                        </div>
                      ))}
                  </div>
                </Card>
              )}

              {/* Ignored Transactions */}
              {selectedStatement.transactions?.filter(t => t.status === 'ignored').length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-4 text-slate-500 flex items-center gap-2">
                    <XCircle className="h-5 w-5" />
                    Ignored ({selectedStatement.transactions.filter(t => t.status === 'ignored').length})
                  </h3>
                  <div className="space-y-2">
                    {selectedStatement.transactions
                      .filter(t => t.status === 'ignored')
                      .map(txn => (
                        <div key={txn.id} className="p-3 border rounded-lg flex justify-between items-center opacity-60">
                          <div>
                            <p className="font-medium">{txn.description}</p>
                            <p className="text-sm text-muted-foreground">{txn.ignore_reason}</p>
                          </div>
                          <p className="text-lg font-mono">{formatCurrency(txn.amount)}</p>
                        </div>
                      ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Bank Statement
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with bank transactions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Bank Name *</Label>
              <Input
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="e.g., Al Rajhi Bank"
              />
            </div>

            <div>
              <Label>Account Number</Label>
              <Input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="Optional"
              />
            </div>

            <div>
              <Label>CSV File *</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Expected columns: Date, Description, Debit, Credit, Balance
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpload} disabled={!uploadFile || !bankName || loading}>
                {loading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Match Dialog */}
      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Match Transaction
            </DialogTitle>
            <DialogDescription>
              Select an invoice to match with this transaction
            </DialogDescription>
          </DialogHeader>

          {selectedTransaction && (
            <div className="mt-4">
              <Card className="p-3 bg-blue-50 mb-4">
                <p className="font-semibold">{selectedTransaction.description}</p>
                <p className="text-sm text-muted-foreground">{selectedTransaction.date}</p>
                <p className={`text-xl font-bold ${selectedTransaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(selectedTransaction.amount)} SAR
                </p>
              </Card>

              <h4 className="font-semibold mb-2">Available Invoices</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {invoices
                  .filter(inv => inv.status !== 'paid')
                  .map(inv => (
                    <div
                      key={inv.id}
                      className="p-3 border rounded-lg flex justify-between items-center hover:bg-slate-50 cursor-pointer"
                      onClick={() => handleManualMatch(inv.id)}
                    >
                      <div>
                        <p className="font-medium">{inv.invoice_number}</p>
                        <p className="text-sm text-muted-foreground">{inv.customer_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(inv.total || inv.subtotal)} SAR</p>
                        <Badge>{inv.status}</Badge>
                      </div>
                    </div>
                  ))}
                {invoices.filter(inv => inv.status !== 'paid').length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No unpaid invoices available</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
